import React, { useState, useEffect } from 'react'
import { TextInput } from '@toasttab/buffet-pui-text-input'
import { isValidEmail } from '@toasttab/email-validation'
import { t } from '@local/translations'
import cx from 'classnames'

type Params = {
  emailInput: string
  setEmailInput: (email: string) => void
  disabled?: boolean
  required?: boolean
  showLabel?: boolean
  placeholder?: string
  className?: string
}

export const EmailInput = ({
  emailInput,
  setEmailInput,
  disabled = false,
  required = false,
  showLabel = true,
  placeholder = '',
  className = ''
}: Params) => {
  const [hasBlurred, setHasBlurred] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [isValid, setIsValid] = useState(true)

  // Reset validation state when input is cleared externally (e.g., tab switch)
  useEffect(() => {
    if (!emailInput.trim()) {
      setHasBlurred(false)
      setErrorMessage('')
      setIsValid(true)
    }
  }, [emailInput])

  const validateEmail = (email: string) => {
    if (required && !email.trim()) {
      return t('form.errors.email.required')
    }
    if (email.trim() && !isValidEmail(email)) {
      return t('form.errors.email.invalid')
    }
    return ''
  }

  const onChangeEmail = (e: { target: { value: string } }) => {
    const value = e?.target?.value ?? ''
    setEmailInput(value)

    // For required fields, validate on change if field has been blurred
    if (required && hasBlurred) {
      const error = validateEmail(value)
      setErrorMessage(error)
      setIsValid(!error)
    } else {
      // For required fields that haven't been blurred yet, or non-required fields
      // Clear any existing errors
      setErrorMessage('')
      setIsValid(true)
    }
  }

  const onBlur = () => {
    setHasBlurred(true)
    const error = validateEmail(emailInput)
    setErrorMessage(error)
    setIsValid(!error)
  }

  return (
    <div className='w-full'>
      <TextInput
        label={showLabel ? t('form.labels.email') : ''}
        name='email'
        className={cx('w-full', className)}
        value={emailInput}
        onChange={onChangeEmail}
        onBlur={onBlur}
        disabled={disabled}
        required={required}
        invalid={!isValid}
        errorText={errorMessage}
        aria-label={t('form.labels.email')}
        placeholder={placeholder}
      />
    </div>
  )
}
